import { useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchFormDetail,
  publishDraftVersion,
  createDraftVersion,
  fetchFormVersion,
} from "../lib/api-client";

function FormVersionsPage(): JSX.Element {
  const { formId } = useParams<{ teamId: string; formId: string }>();
  const queryClient = useQueryClient();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishingVersionId, setPublishingVersionId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const formQuery = useQuery({
    queryKey: ["form-detail", formId],
    queryFn: () => fetchFormDetail(formId!),
    enabled: Boolean(formId),
  });

  const versionQuery = useQuery({
    queryKey: ["form-version", selectedVersionId],
    queryFn: () => fetchFormVersion(selectedVersionId!),
    enabled: Boolean(selectedVersionId),
  });

  const compareVersionQuery = useQuery({
    queryKey: ["form-version", compareVersionId],
    queryFn: () => fetchFormVersion(compareVersionId!),
    enabled: Boolean(compareVersionId),
  });

  const publishMutation = useMutation({
    mutationFn: async (versionId: string) => {
      return publishDraftVersion(versionId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-detail", formId] });
      setShowPublishDialog(false);
      setPublishingVersionId(null);
      setToast({ type: "success", message: "Version published successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to publish version.",
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (versionId: string) => {
      const version = await fetchFormVersion(versionId);
      return createDraftVersion(formId!, version.schema);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-detail", formId] });
      setToast({ type: "success", message: "Version duplicated successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to duplicate version.",
      });
    },
  });

  const form = formQuery.data?.form;
  const versions = formQuery.data?.versions ?? [];
  const selectedVersion = versionQuery.data;
  const compareVersion = compareVersionQuery.data;

  const containerStyle: CSSProperties = {
    display: "flex",
    gap: "1.5rem",
    height: "100%",
  };

  const sidebarStyle: CSSProperties = {
    width: "300px",
    borderRight: "1px solid var(--color-border)",
    overflowY: "auto",
    padding: "1.5rem",
  };

  const mainStyle: CSSProperties = {
    flex: 1,
    overflowY: "auto",
    padding: "1.5rem",
  };

  const versionItemStyle: CSSProperties = {
    padding: "0.75rem",
    marginBottom: "0.5rem",
    borderRadius: "var(--radius-base)",
    border: "1px solid var(--color-border)",
    cursor: "pointer",
    transition: "all 0.15s",
  };

  return (
    <div style={containerStyle}>
      {/* Sidebar - Version List */}
      <aside style={sidebarStyle}>
        <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
          Versions ({versions.length})
        </h2>

        {versions.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem" }}>
            No versions yet
          </p>
        ) : (
          <div>
            {versions.map((version) => (
              <div
                key={version.id}
                style={{
                  ...versionItemStyle,
                  background:
                    selectedVersionId === version.id
                      ? "var(--color-primary-soft)"
                      : "transparent",
                  borderColor:
                    selectedVersionId === version.id
                      ? "var(--color-primary)"
                      : "var(--color-border)",
                }}
                onClick={() => setSelectedVersionId(version.id)}
              >
                <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>
                  v{version.versionNumber}
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    marginTop: "0.25rem",
                  }}
                >
                  {version.status}
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    marginTop: "0.25rem",
                  }}
                >
                  {new Date(version.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main style={mainStyle}>
        {!selectedVersionId ? (
          <div style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
            <p>Select a version to view details</p>
          </div>
        ) : selectedVersion ? (
          <div>
            <div style={{ marginBottom: "2rem" }}>
              <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                Version {selectedVersion.versionNumber}
              </h1>
              <p style={{ color: "var(--color-text-muted)" }}>
                Status: <strong>{selectedVersion.status}</strong>
              </p>
              <p style={{ color: "var(--color-text-muted)", marginTop: "0.25rem" }}>
                Created: {new Date(selectedVersion.createdAt).toLocaleString()}
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "2rem" }}>
              {selectedVersion.status === "draft" && (
                <button
                  type="button"
                  className="button primary"
                  onClick={() => {
                    setPublishingVersionId(selectedVersion.id);
                    setShowPublishDialog(true);
                  }}
                  disabled={publishMutation.isPending}
                >
                  {publishMutation.isPending ? "Publishing..." : "Publish"}
                </button>
              )}
              <button
                type="button"
                className="button ghost"
                onClick={() => duplicateMutation.mutate(selectedVersion.id)}
                disabled={duplicateMutation.isPending}
              >
                {duplicateMutation.isPending ? "Duplicating..." : "Duplicate"}
              </button>
              <button
                type="button"
                className="button ghost"
                onClick={() => setCompareVersionId(compareVersionId ? null : selectedVersion.id)}
              >
                {compareVersionId ? "Hide Comparison" : "Compare"}
              </button>
            </div>

            {/* Version Details */}
            <div style={{ background: "var(--color-bg-solid)", padding: "1rem", borderRadius: "var(--radius-base)" }}>
              <h3 style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.75rem" }}>
                Schema
              </h3>
              <pre
                style={{
                  background: "var(--color-surface)",
                  padding: "0.75rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  overflow: "auto",
                  fontSize: "0.75rem",
                  maxHeight: "300px",
                }}
              >
                {JSON.stringify(selectedVersion.schema, null, 2)}
              </pre>
            </div>

            {/* Comparison View */}
            {compareVersion && compareVersionId !== selectedVersion.id && (
              <div style={{ marginTop: "2rem" }}>
                <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>
                  Comparison: v{selectedVersion.versionNumber} vs v{compareVersion.versionNumber}
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div style={{ background: "var(--color-bg-solid)", padding: "1rem", borderRadius: "var(--radius-base)" }}>
                    <h4 style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.75rem" }}>
                      Version {selectedVersion.versionNumber}
                    </h4>
                    <pre
                      style={{
                        background: "var(--color-surface)",
                        padding: "0.75rem",
                        borderRadius: "calc(var(--radius-base) - 2px)",
                        overflow: "auto",
                        fontSize: "0.75rem",
                        maxHeight: "300px",
                      }}
                    >
                      {JSON.stringify(selectedVersion.schema, null, 2)}
                    </pre>
                  </div>
                  <div style={{ background: "var(--color-bg-solid)", padding: "1rem", borderRadius: "var(--radius-base)" }}>
                    <h4 style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.75rem" }}>
                      Version {compareVersion.versionNumber}
                    </h4>
                    <pre
                      style={{
                        background: "var(--color-surface)",
                        padding: "0.75rem",
                        borderRadius: "calc(var(--radius-base) - 2px)",
                        overflow: "auto",
                        fontSize: "0.75rem",
                        maxHeight: "300px",
                      }}
                    >
                      {JSON.stringify(compareVersion.schema, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
            <p>Loading version details...</p>
          </div>
        )}

        {/* Publish Confirmation Dialog */}
        {showPublishDialog && publishingVersionId && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowPublishDialog(false)}
          >
            <div
              style={{
                background: "var(--color-surface)",
                borderRadius: "var(--radius-base)",
                padding: "2rem",
                maxWidth: "400px",
                boxShadow: "var(--shadow-lg)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem" }}>
                Publish Version?
              </h2>
              <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
                This will make this version available to form respondents. This action cannot be undone.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => setShowPublishDialog(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button primary"
                  onClick={() => publishMutation.mutate(publishingVersionId)}
                  disabled={publishMutation.isPending}
                >
                  {publishMutation.isPending ? "Publishing..." : "Publish"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <div
            style={{
              position: "fixed",
              bottom: "1.5rem",
              right: "1.5rem",
              background: toast.type === "success" ? "var(--color-success)" : "var(--color-critical)",
              color: "white",
              padding: "1rem 1.5rem",
              borderRadius: "var(--radius-base)",
              boxShadow: "var(--shadow-lg)",
              zIndex: 1001,
            }}
          >
            {toast.message}
          </div>
        )}
      </main>
    </div>
  );
}

export default FormVersionsPage;
